import React, { useState, useEffect } from 'react';

import styled from 'styled-components';
import NumberFormat from 'react-number-format';
import Select from 'components/ui/SelectField';
import { Input } from 'components/ui/Input';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM } from './AddPurchaseOrderDrawer';
import { formatMoney } from 'helpers/Formatter';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

const StyledFormContainer = styled.div``;

const StyledFormRow = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StyledFormItem = styled.div`
  width: 100%;
  margin: 0px 8px;
`;

const StyledFormItemRightAligned = styled.div`
  width: 100%;
  margin: 0px 8px;
  text-align: right;
`;

const StyledFormItemLabel = styled.div`
  margin-bottom: 5px;
`;

const StyledError = styled.div`
  color: ${colors.YUHU_RED};
`;

const StyledNumberInput = styled(NumberFormat)`
  width: 100%;
  border-radius: 5px !important;
  border: 1px solid #d0dbfd !important;
  padding: 7px 10px;
  &:focus {
    outline: none !important;
    border-color: #230cc2 !important;
  }
`;

const NewPurchaseOrderCatalogItem = ({
  setFieldValue,
  vendor,
  purchase_order_item,
  units,
  setPurchaseOrderFormStep,
  formErrors,
}) => {
  const [vendorCatalogCategories, setVendorCatalogCategories] = useState([]);
  const [vendorCatalogCategoryItems, setVendorCatalogCategoryItems] = useState([]);

  useEffect(
    () => {
      let uniqueCatalogCategories = vendor.catalog_items.reduce(
        (acc, x) => acc.concat(acc.find(y => y.item_category_id === x.item_category_id) ? [] : [x]),
        []
      );
      setVendorCatalogCategories(uniqueCatalogCategories);

      if (purchase_order_item.item_category_id) {
        let categoryItems = vendor.catalog_items.filter(
          item => item.item_category_id === purchase_order_item.item_category_id
        );
        setVendorCatalogCategoryItems(categoryItems);
      }
      if (purchase_order_item.quantity && purchase_order_item.estimated_cost) {
        let totalCost =
          Math.round(purchase_order_item.estimated_cost * purchase_order_item.quantity * 100) / 100;
        setFieldValue('purchase_order_item.total_cost', totalCost);
      }
    },
    [
      purchase_order_item.estimated_cost,
      purchase_order_item.item_category_id,
      purchase_order_item.quantity,
      vendor.catalog_items,
      setFieldValue,
    ]
  );
  let quantities = [];
  for (let i = 1; i < 100; i++) {
    quantities.push(i);
  }

  const handleCategoryChange = value => {
    let selectedCategoryItems = vendor.catalog_items.filter(
      item => item.item_category_id === value
    );

    setVendorCatalogCategoryItems(selectedCategoryItems);
    setFieldValue('purchase_order_item.item_category_id', value);
    setFieldValue('purchase_order_item.item_name', '');
    setFieldValue('purchase_order_item.item_number', '');
    setFieldValue('catalog_form_errors.item_category_id', false);
  };

  const handleCatalogCategoryItemChange = value => {
    let selectedItem = vendor.catalog_items.find(item => item.item_description === value);

    setFieldValue('purchase_order_item.item_name', value);
    setFieldValue('purchase_order_item.item_description', value);
    if (selectedItem && selectedItem.item_number) {
      setFieldValue('purchase_order_item.item_number', selectedItem.item_number);
    }
    setFieldValue('purchase_order_item.estimated_cost', selectedItem.item_price);
    setFieldValue('purchase_order_item.total_cost', '');
    setFieldValue('purchase_order_item.quantity', '');
    setFieldValue('catalog_form_errors.item_name', false);
  };

  const handleQuantityChange = e => {
    let quantity = parseInt(e.target.value);
    let totalCost = Math.round(purchase_order_item.estimated_cost * quantity * 100) / 100;
    setFieldValue('purchase_order_item.quantity', quantity);
    setFieldValue('purchase_order_item.total_cost', totalCost);
    setFieldValue('catalog_form_errors.quantity', false);
  };

  return (
    <StyledFormContainer>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.indicates')}
          </StyledFormItemLabel>
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.category')}*
          </StyledFormItemLabel>
          <Select
            showSearch
            placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
            value={purchase_order_item.item_category_id}
            size="large"
            onChange={handleCategoryChange}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            {vendorCatalogCategories.map((item, i) => (
              <Select.Option value={item.item_category_id} key={`${item.item_category_id}_${i}`}>
                {item.item_category}
              </Select.Option>
            ))}
          </Select>
          {formErrors.item_category_id && (
            <StyledError>{getTranslatedString('required')}</StyledError>
          )}
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.items')}*
          </StyledFormItemLabel>
          <Select
            showSearch
            placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
            value={purchase_order_item.item_name}
            size="large"
            onChange={handleCatalogCategoryItemChange}
            optionLabelProp="label"
            filterOption={(input, option) =>
              option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            {renderCategoryItemOptions(vendorCatalogCategoryItems)}
          </Select>
          {formErrors.item_name && <StyledError>{getTranslatedString('required')}</StyledError>}
          <StyledFormItemLabel>
            <div style={{ marginTop: '10px' }}>
              {getTranslatedString(
                'landlordPortal.newPurchaseOrderCatalogItem.dontSeeTheItem'
              )}{' '}
              <span
                style={{ color: `${colors.ULTRAMARINE_BLUE}` }}
                onClick={() => setPurchaseOrderFormStep(NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM)}
              >
                {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.createNewItem')}
              </span>
            </div>
          </StyledFormItemLabel>
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.quantity')}*
          </StyledFormItemLabel>
          <StyledNumberInput
            value={purchase_order_item.quantity}
            thousandSeparator={true}
            decimalScale={0}
            placeholder={getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.enter'
            )}
            onChange={handleQuantityChange}
          />
          {formErrors.quantity && <StyledError>{getTranslatedString('required')}</StyledError>}
        </StyledFormItem>
        <StyledFormItemRightAligned>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.estTotalCost')}
          </StyledFormItemLabel>
          <Input
            type="text"
            value={
              purchase_order_item.quantity
                ? formatMoney(purchase_order_item.total_cost)
                : getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.calculating')
            }
            disabled={true} //read only
            style={{
              padding: '7px 10px',
              textAlign: 'right',
              border: 'none',
            }}
          />
        </StyledFormItemRightAligned>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.unit')}
          </StyledFormItemLabel>
          <Select
            showSearch
            placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
            size="large"
            value={purchase_order_item.unit_id}
            onChange={value => {
              setFieldValue('purchase_order_item.unit_id', value);
              setFieldValue(
                'purchase_order_item.unit_number',
                units.find(unit => unit.value === value).label
              );
            }}
          >
            {units.map(unit => (
              <Select.Option value={unit.value} key={`unit_number_${unit.value}`}>
                {unit.label}
              </Select.Option>
            ))}
          </Select>
        </StyledFormItem>
      </StyledFormRow>
    </StyledFormContainer>
  );
};

const ColCutoff = styled(Col)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const renderCategoryItemOptions = vendorCatalogCategoryItems => {
  return vendorCatalogCategoryItems.map((item, i) => {
    return (
      <Select.Option
        value={item.item_description}
        label={item.item_description}
        key={`${item.catalog_item_id}_${i}`}
      >
        <div>
          <Row gutter={16}>
            <ColCutoff xs={18}>{item.item_description}</ColCutoff>
            <Col xs={6}>
              <span style={{ float: 'right' }}>{formatMoney(item.item_price)}</span>
            </Col>
            {item.item_number && (
              <ColCutoff xs={18}>
                <small>{item.item_number}</small>
              </ColCutoff>
            )}
          </Row>
        </div>
      </Select.Option>
    );
  });
};

export default NewPurchaseOrderCatalogItem;
